import { useMemo } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import {
  CalendarClock,
  CreditCard,
  FileText,
  LayoutDashboard,
  Lock,
  Package,
  Phone,
  Plus,
  Receipt,
  Scale,
  Shield,
  Ticket,
  Users,
} from 'lucide-react'
import { AppShell, type NavCategory, type NavItem } from '@/components/layout/AppShell.tsx'
import { useCustomerAuthStore } from '../stores/auth'
import { ImpersonationBanner } from './ImpersonationBanner'
import { hasAnyModule, useCustomerModules } from '../hooks/useCustomerModules'

interface CustomerNavItem extends NavItem {
  /**
   * Mindestens eine der angegebenen Modul-IDs muss erlaubt sein, damit der
   * Eintrag sichtbar ist. Fehlt das Feld, ist der Eintrag immer sichtbar.
   * Default-Allow: solange die Backend-Permissions noch nicht geladen sind
   * oder das Modul nicht explizit auf `false` gesetzt wurde, gilt es als
   * erlaubt.
   */
  requireModules?: string[]
}

interface CustomerNavCategory {
  id: string
  label: string
  items: CustomerNavItem[]
}

const customerNavCategories: CustomerNavCategory[] = [
  {
    id: 'ueberblick',
    label: 'Übersicht',
    items: [
      { to: '/', icon: LayoutDashboard, label: 'Start', description: 'Offene Tickets & Schnellzugriff', end: true },
      { to: '/sicherheitsstatus', icon: Shield, label: 'Sicherheitsstatus', description: 'IT-Sicherheit auf einen Blick', requireModules: ['customer-contracts'] },
    ],
  },
  {
    id: 'support',
    label: 'Support',
    items: [
      { to: '/tickets', icon: Ticket, label: 'Meine Tickets', description: 'Offene und geschlossene Tickets', end: true, requireModules: ['customer-tickets-own', 'customer-tickets-all', 'tickets'] },
      { to: '/tickets/neu', icon: Plus, label: 'Neues Ticket', description: 'Anfrage an den Support', end: true, requireModules: ['customer-tickets-own', 'customer-tickets-all', 'tickets'] },
      { to: '/tickets/neu?followUp=callback', icon: Phone, label: 'Rückruf anfordern', description: 'Wir rufen zurück', requireModules: ['customer-tickets-own', 'customer-tickets-all', 'tickets'] },
      { to: '/tickets/neu?followUp=appointment', icon: CalendarClock, label: 'Termin vereinbaren', description: 'Termin mit dem Support buchen', requireModules: ['customer-tickets-own', 'customer-tickets-all', 'tickets'] },
    ],
  },
  {
    id: 'zugriffsmatrix',
    label: 'Zugriffsmatrix',
    items: [
      { to: '/zugriffsmatrix', icon: Lock, label: 'Zugriffsmatrix', description: 'Berechtigungen auf Freigaben & Gruppen', requireModules: ['customer-access-matrix', 'it-structures'] },
    ],
  },
  {
    id: 'mitarbeiter',
    label: 'Mitarbeiter',
    items: [
      { to: '/mitarbeiter', icon: Users, label: 'Mitarbeiter', description: 'Ansprechpartner in TANSS', requireModules: ['customer-employees-manage', 'customer-employees'] },
    ],
  },
  {
    id: 'dokumente',
    label: 'Dokumente',
    items: [
      { to: '/dokumente/angebote', icon: FileText, label: 'Angebote', description: 'Aktuelle Angebote', requireModules: ['customer-sap-quotes', 'customer-sap'] },
      { to: '/dokumente/auftraege', icon: FileText, label: 'Aufträge', description: 'Erteilte Aufträge', requireModules: ['customer-sap-quotes', 'customer-sap'] },
      { to: '/dokumente/lieferscheine', icon: Package, label: 'Lieferscheine', description: 'Versand & Lieferungen', requireModules: ['customer-sap-quotes', 'customer-sap'] },
      { to: '/dokumente/rechnungen', icon: Receipt, label: 'Rechnungen', description: 'Ausgangsrechnungen', requireModules: ['customer-sap-invoices', 'customer-sap'] },
      { to: '/dokumente/gutschriften', icon: FileText, label: 'Gutschriften', description: 'Gutschriften & Retouren', requireModules: ['customer-sap-invoices', 'customer-sap'] },
      { to: '/dokumente/opos', icon: Scale, label: 'Offene Posten', description: 'Fällige Rechnungen', requireModules: ['customer-sap-opos', 'customer-sap'] },
    ],
  },
  {
    id: 'vertraege',
    label: 'Verträge',
    items: [
      { to: '/vertraege', icon: CreditCard, label: 'Meine Verträge', description: 'Laufende Verträge & Services', requireModules: ['customer-contracts'] },
    ],
  },
]

export function CustomerLayout() {
  const user = useCustomerAuthStore((s) => s.user)
  const logout = useCustomerAuthStore((s) => s.logout)
  const navigate = useNavigate()
  const moduleState = useCustomerModules()

  const filteredNav = useMemo<NavCategory[]>(() => {
    const out: NavCategory[] = []
    for (const cat of customerNavCategories) {
      const items: NavItem[] = []
      for (const item of cat.items) {
        if (item.requireModules && !hasAnyModule(moduleState, item.requireModules)) continue
        const { requireModules, ...rest } = item
        void requireModules
        items.push(rest)
      }
      if (items.length > 0) out.push({ id: cat.id, label: cat.label, items })
    }
    return out
  }, [moduleState])

  return (
    <AppShell
      brand={{
        homeTo: '/',
        title: 'implec',
        subtitle: 'Kundenportal',
      }}
      nav={filteredNav}
      user={user}
      onLogout={() => {
        logout()
        navigate('/login')
      }}
      topBar={<ImpersonationBanner />}
    >
      <Outlet />
    </AppShell>
  )
}
